/**
 * DocProcessor — converts Y.Docs to docHtml + fullTextIndex.
 *
 * Architecture:
 * - In-memory `Set<string>` tracks dirty document IDs (zero IDB overhead)
 * - Processing uses `requestIdleCallback` to avoid blocking frames
 * - Batches of 50 docs: bulkGet → DexieYProvider.load → convert → skip-if-unchanged → bulkUpdate
 * - DexieYProvider.load() is required because y-dexie returns unhydrated Y.Doc shells
 *   from get()/bulkGet() — updates live in $totodo.doc_updates
 * - Re-checks the Set after each batch (new items may arrive from trigger)
 *
 * Two entry points:
 * - `markDirty(id)` — called by defineYDocTrigger (real-time sync), 500ms debounce
 * - `markManyDirty(ids)` — called by cursor-sweep (startup), immediate processing
 *
 * Crash safety:
 * - In-memory Set is volatile — lost on crash/reload
 * - Cursor-sweep at next startup catches anything the Set missed
 * - Y.Doc is the source of truth; docHtml is a regenerable cache
 *
 * @see startupSweep.ts for the cursor-based initial scan
 * @see index.ts for defineYDocTrigger registration
 */

import { DexieYProvider } from "y-dexie"
import { db, type DbObject } from "@/db/schema"
import type { Doc as YDoc } from "yjs"
import { yDocToHtml, isEmptyDocHtml } from "./yDocToHtml"
import { extractSearchKeywords } from "./extractSearchKeywords"

/** Maximum documents to process in one batch before yielding. */
const BATCH_SIZE = 20

/** Debounce interval for trigger-fired dirty marks (ms). */
const TRIGGER_DEBOUNCE_MS = 500

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Yield to the main thread so the browser can paint/handle events.
 * Uses `requestIdleCallback` when available, falls back to setTimeout.
 */
function yieldToMain(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(() => resolve(), { timeout: 2000 })
    } else {
      setTimeout(resolve, 16) // ~1 frame at 60fps
    }
  })
}

// ---------------------------------------------------------------------------
// DocProcessor
// ---------------------------------------------------------------------------

class DocProcessor {
  private dirtyIds = new Set<string>()
  private isRunning = false
  private scheduled = false
  private debounceTimer: ReturnType<typeof setTimeout> | null = null

  /**
   * Mark a single document as dirty (needs docHtml regeneration).
   * Called by defineYDocTrigger — debounced to batch rapid sync changes.
   */
  markDirty(id: string): void {
    this.dirtyIds.add(id)
    // Debounce: wait for burst of trigger fires to settle
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null
      this.scheduleRun()
    }, TRIGGER_DEBOUNCE_MS)
  }

  /**
   * Mark multiple documents as dirty, then start processing immediately.
   * Called by cursor-sweep at startup — no debounce needed.
   */
  markManyDirty(ids: string[]): void {
    for (const id of ids) {
      this.dirtyIds.add(id)
    }
    if (ids.length > 0) {
      this.scheduleRun()
    }
  }

  /**
   * Schedule a processing run via requestIdleCallback. Deduplicates rapid calls.
   */
  private scheduleRun(): void {
    if (this.scheduled || this.isRunning) return
    this.scheduled = true

    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(() => void this.run(), { timeout: 3000 })
    } else {
      setTimeout(() => void this.run(), 100)
    }
  }

  /**
   * Main processing loop. Drains the dirty Set in batches of BATCH_SIZE.
   * Yields to the main thread between batches.
   */
  private async run(): Promise<void> {
    this.scheduled = false
    if (this.isRunning) return
    this.isRunning = true

    try {
      let processedTotal = 0

      while (this.dirtyIds.size > 0) {
        // Take next batch from dirty Set
        const batchIds: string[] = []
        for (const id of this.dirtyIds) {
          batchIds.push(id)
          if (batchIds.length >= BATCH_SIZE) break
        }
        // Remove from Set immediately (before async work)
        for (const id of batchIds) {
          this.dirtyIds.delete(id)
        }

        // Bulk-read all rows in one IDB call
        const rows = await db.totodo.bulkGet(batchIds)

        const updates: { key: string; changes: Partial<DbObject> }[] = []

        for (let i = 0; i < batchIds.length; i++) {
          const id = batchIds[i]!
          const row = rows[i]

          if (!row) continue // Row was deleted

          try {
            // Check if the row has a Y.Doc shell
            const doc = row.doc
            if (!doc || typeof doc !== "object" || !("getXmlFragment" in doc)) {
              continue // No Y.Doc — nothing to process
            }

            const yDoc = doc as YDoc

            // y-dexie returns an UNHYDRATED Y.Doc shell from get()/bulkGet().
            // Updates are stored in $totodo.doc_updates and only applied
            // when DexieYProvider.load() is called.
            let provider: DexieYProvider | null = null
            try {
              provider = DexieYProvider.load(yDoc)
              await provider.whenLoaded
            } catch {
              // DexieYProvider.load can fail for docs without updates
              if (provider) {
                try { DexieYProvider.release(yDoc) } catch { /* ignore */ }
              }
              continue
            }

            try {
              const html = yDocToHtml(yDoc)
              const isEmpty = isEmptyDocHtml(html)
              const newDocHtml = isEmpty ? "" : html
              const newKeywords = isEmpty ? [] : extractSearchKeywords(html)

              // Skip-if-unchanged: avoid unnecessary IDB writes + liveQuery re-renders
              if (newDocHtml !== (row.docHtml ?? "")) {
                updates.push({
                  key: id,
                  changes: { docHtml: newDocHtml, fullTextIndex: newKeywords },
                })
              }
            } finally {
              try { DexieYProvider.release(yDoc) } catch { /* ignore */ }
            }
          } catch (error) {
            console.warn("[DocProcessor] Failed to process doc:", id, error)
          }
        }

        // Batch write only changed rows
        if (updates.length > 0) {
          await db.totodo.bulkUpdate(updates)
        }

        processedTotal += batchIds.length

        // Yield to main thread between batches
        await yieldToMain()
      }

      if (processedTotal > 0) {
        console.info(`[DocProcessor] Processed ${processedTotal} documents`)
      }
    } catch (error) {
      console.error(
        "[DocProcessor] Processing run failed:",
        error instanceof Error ? error.message + "\n" + error.stack : JSON.stringify(error),
      )
    } finally {
      this.isRunning = false

      // Check if new items were added during processing (from trigger)
      if (this.dirtyIds.size > 0) {
        this.scheduleRun()
      }
    }
  }
}

/** Singleton processor instance. */
export const docProcessor = new DocProcessor()
